/**
 * Wave 29 Chunk 2 — Employee detail (Team / Me).
 *
 * Tabs:
 *   - Overview       (read-only summary)
 *   - Profile        (contact + HR fields; HR can edit)
 *   - Documents      (vault + expiry)
 *   - Job cards      (deep-link list)
 *   - Payslips       (SimplePay-synced)
 *
 * Security:
 *   - Backend enforces self-or-HR on every endpoint (team_rbac).
 *   - This page treats `isSelf || isHr` as the access check for visibility,
 *     but the backend is the source of truth — never weaken it client-side.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  User, Briefcase, FileText, ClipboardList, BanknoteIcon,
  Save, Loader2, Lock, AlertTriangle, Calendar, ShieldCheck, Trash2,
  Upload, Download, CheckCircle2, Clock, Trophy, CalendarDays, Wallet, Pencil,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import { uploadFile, uploadUrl } from "../lib/uploads";
import { useBranding } from "../context/TenantBrandingContext";
import WellnessCard from "../components/team/WellnessCard";
import { EmployeeScorecardTab } from "../components/scorecard/ProjectScorecardTab";
import EmployeeProfileForm from "../components/team/EmployeeProfileForm";
import DatePickerInput from "../components/DatePickerInput";
import VoiceTextarea from "../components/VoiceTextarea";
import EmployeeLoansTab from "../components/team/EmployeeLoansTab";
import PhoneInput from "../components/PhoneInput";
import { EntityShell } from "../components/shell";

const TABS = [
  { key: "overview", label: "Overview", icon: User },
  { key: "profile", label: "Profile", icon: Briefcase },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "jobcards", label: "Job cards", icon: ClipboardList },
  { key: "payslips", label: "Payslips", icon: BanknoteIcon },
  { key: "loans", label: "Loans", icon: Wallet },
  { key: "scorecard", label: "Penalties and Rewards", icon: Wallet },
];

export default function EmployeeDetail({ selfMode = false }) {
  const { uid: uidParam } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const uid = selfMode ? user?.id : uidParam;
  const isSelf = user && uid === user.id;
  const isHr = user && ["owner", "admin"].includes(user.role);
  const canEdit = isHr;
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!uid) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const path = selfMode ? "/team/me" : `/team/employees/${uid}`;
        const { data } = await api.get(path);
        if (alive) setEmp(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load employee.");
        if (alive) nav("/team", { replace: true });
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [uid, selfMode, nav]);

  if (!user || !uid) return null;
  if (loading) {
    return <div className="h-40 bg-white/[0.04] rounded animate-pulse" data-testid="employee-loading" />;
  }
  if (!emp) return null;


  // Resilient display-name resolver — Dirk 2026-02-20.
  // The list page was showing "Dirk van Schalkwyk" but the detail page
  // showed "(no name)". Root cause: `emp.name` was empty (user record
  // pre-dates Wave 29 name backfill) and the prior fallback only kicked
  // in for selfMode. Now we cascade through EVERY available signal:
  //   1. emp.name (the canonical staff name)
  //   2. emp.full_name (legacy field)
  //   3. emp.first_name + emp.last_name
  //   4. user.name from auth context (any caller, not just selfMode —
  //      avoids cases where uid matches but selfMode flag is false)
  //   5. emp.email local part (before @)
  //   6. user.email local part
  //   7. literal "(no name)" only as absolute last resort
  const _emailLocal = (e) => (e || "").split("@")[0].trim();
  const isSelfRecord = emp.id === user?.id || emp.email === user?.email;
  const displayName =
    emp.name ||
    emp.full_name ||
    [emp.first_name, emp.last_name].filter(Boolean).join(" ").trim() ||
    (isSelfRecord ? user?.name : "") ||
    _emailLocal(emp.email) ||
    (isSelfRecord ? _emailLocal(user?.email) : "") ||
    "(no name)";
  const displayEmail = emp.email || (isSelfRecord ? user?.email : "") || "";

  // Backend already enforces RBAC; this is a friendly UI guard.
  if (!isSelf && !isHr) {
    return (
      <div className="hv-card p-6 text-center text-sm text-[#a0aabf]">
        <Lock className="w-6 h-6 mx-auto mb-2 opacity-50" />
        You can only view your own profile.
      </div>
    );
  }

  return (
    <EntityShell
      kindLabel={selfMode ? "My profile" : "Employee"}
      title={displayName}
      status={emp.role ? { label: emp.role, tone: "blue" } : undefined}
      backTo={selfMode ? "/dashboard" : "/team"}
      actions={
        <>
          {selfMode && (
            <>
              <Link
                to="/team/leave"
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="me-leave-link"
                title="Apply for leave & see balance"
              >
                <CalendarDays className="w-3.5 h-3.5" /> Leave
              </Link>
              <Link
                to="/team/performance"
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="me-performance-link"
                title="See your leaderboard rank, badges and points"
              >
                <Trophy className="w-3.5 h-3.5" /> Performance
              </Link>
              <Link
                to="/clock"
                className="hv-btn hv-btn-primary text-xs"
                data-testid="me-clock-link"
                title="Open the personal clock"
              >
                <Clock className="w-3.5 h-3.5" /> Clock in / out
              </Link>
            </>
          )}
          {!selfMode && isHr && (
            <GrantPointsButton uid={uid} name={displayName} />
          )}
          {emp.simplepay_id && (
            <span className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-400/40 text-emerald-300 bg-emerald-400/[0.06]">
              <ShieldCheck className="w-3 h-3" /> SimplePay #{emp.simplepay_id}
            </span>
          )}
        </>
      }
      tabs={TABS.filter((t) => t.key !== "scorecard" || isSelf || ["owner", "admin", "manager"].includes(user?.role)).map((t) => ({ key: t.key, label: t.label }))}
      activeTab={tab}
      onTabChange={setTab}
    >
    <div className="space-y-4" data-testid="employee-detail">
      {/* Identity card */}
      <div className="hv-card p-4 sm:p-5">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-16 h-16 rounded-full bg-white/5 overflow-hidden flex items-center justify-center">
            {emp.avatar_url ? (
              <img src={emp.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-[#a0aabf] text-xl font-semibold">{(displayName || "?").slice(0, 2).toUpperCase()}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-[#a0aabf]">{displayEmail}</div>
            <div className="flex items-center gap-1 mt-1 flex-wrap">
              {emp.job_title && <span className="text-[10px] text-[#a0aabf]">{emp.job_title}</span>}
              {emp.employee_number && <span className="text-[10px] text-[#647087]">· #{emp.employee_number}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Wellness check-in — selfMode only (private to the user) */}
      {selfMode && <WellnessCard />}

      {tab === "overview" && <OverviewTab emp={emp} />}
      {tab === "profile" && (
        <>
          {/* Iter 359 — full HR profile form replaces the old ProfileTab.
              StepIndex-driven, 9 sections, per-section save. Identity +
              Pay-rate blocks are embedded inside the form now, so we no
              longer render them separately. */}
          <EmployeeProfileForm
            emp={emp}
            uid={uid}
            canEdit={canEdit}
            selfMode={!!selfMode}
            isHr={isHr}
            onSaved={(data) => {
              if (data) setEmp((prev) => ({ ...(prev || {}), ...data }));
            }}
          />
        </>
      )}
      {tab === "documents" && <DocumentsTab uid={uid} isSelf={isSelf} isHr={isHr} />}
      {tab === "jobcards" && <JobCardsTab uid={uid} />}
      {tab === "payslips" && <PayslipsTab uid={uid} />}
      {tab === "loans" && <EmployeeLoansTab uid={uid} isHr={isHr} />}
      {tab === "scorecard" && <EmployeeScorecardTab uid={uid} />}
    </div>
    </EntityShell>
  );
}

function OverviewTab({ emp }) {
  return (
    <div className="hv-card p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
        <Cell label="Department" value={emp.department} />
        <Cell label="Job title" value={emp.job_title} />
        <Cell label="Employee #" value={emp.employee_number} />
        <Cell label="Started" value={emp.appointment_date} />
        <Cell label="Status" value={emp.is_active === false ? "Inactive" : "Active"} />
        <Cell label="Pay frequency" value={emp.pay_frequency} />
      </div>
      {emp._thin && (
        <div className="mt-3 text-[11px] text-amber-300 inline-flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> No HR record yet — only the user account exists.
        </div>
      )}
    </div>
  );
}

function Cell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#647087]">{label}</div>
      <div className="text-[#d7dde8] mt-0.5">{value || "—"}</div>
    </div>
  );
}

function ProfileTab({ emp, canEdit, onSaved }) {
  // Wave 27.6 (Dirk, 2026-02-20) — fields used to be open text inputs on
  // arrival, so the smallest scroll-touch on a phone would mutate them.
  // Now everything is read-only by default; HR must click "Edit profile"
  // to unlock the fields. Cancel reverts the draft to the original `emp`
  // snapshot — no risk of a half-edit being saved.
  const initialFrom = (e) => ({
    job_title: e.job_title || "",
    employee_number: e.employee_number || "",
    department: e.department || "",
    appointment_date: e.appointment_date || "",
    birthdate: e.birthdate || "",
    cell_no: e.cell_no || "",
    bus_tel_no: e.bus_tel_no || "",
    id_number: e.id_number || "",
    income_tax_number: e.income_tax_number || "",
    pay_rate: e.pay_rate || "",
    pay_frequency: e.pay_frequency || "",
    overtime_exempt: e.overtime_exempt || false,
    is_active: e.is_active !== false,
    notes: e.notes || "",
  });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => initialFrom(emp));
  const [busy, setBusy] = useState(false);
  // Re-snapshot when the `emp` prop changes (e.g. after a save returns
  // the fresh document) so a subsequent Edit starts from the new state.
  useEffect(() => { setForm(initialFrom(emp)); }, [emp]);

  const startEdit = () => { setForm(initialFrom(emp)); setEditing(true); };
  const cancelEdit = () => { setForm(initialFrom(emp)); setEditing(false); };

  const save = async () => {
    if (!canEdit || !editing) return;
    setBusy(true);
    try {
      const body = { user_id: emp.user_id, ...form };
      // Coerce empties to null so we don't write empty strings.
      Object.keys(body).forEach((k) => { if (body[k] === "") body[k] = null; });
      body.pay_rate = body.pay_rate ? Number(body.pay_rate) : null;
      const { data } = await api.put(`/team/employees/${emp.user_id}`, body);
      onSaved(data);
      toast.success("Saved.");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const fieldsLocked = !editing;
  return (
    <div className="hv-card p-5 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#647087]">
          {editing ? (
            <span className="text-[#1FB6C1]">Editing — unsaved changes will be lost on Cancel</span>
          ) : "Read-only view"}
        </div>
        {canEdit && !editing && (
          <button
            onClick={startEdit}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="emp-profile-edit-toggle"
          >
            <Pencil className="w-3.5 h-3.5" /> Edit profile
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Job title" value={form.job_title} disabled={fieldsLocked} onChange={(v) => setForm({ ...form, job_title: v })} testId="emp-job-title" />
        <Field label="Employee #" value={form.employee_number} disabled={fieldsLocked} onChange={(v) => setForm({ ...form, employee_number: v })} testId="emp-number" />
        <Field label="Department" value={form.department} disabled={fieldsLocked} onChange={(v) => setForm({ ...form, department: v })} testId="emp-dept" />
        <Field label="Appointment date" value={form.appointment_date} type="date" disabled={fieldsLocked} onChange={(v) => setForm({ ...form, appointment_date: v })} testId="emp-appt" />
        <Field label="Birthdate" value={form.birthdate} type="date" disabled={fieldsLocked} onChange={(v) => setForm({ ...form, birthdate: v })} testId="emp-bday" />
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#647087] mb-1">Cell number</label>
          <PhoneInput
            value={form.cell_no || ""}
            onChange={(v) => setForm({ ...form, cell_no: v })}
            disabled={fieldsLocked}
            testId="emp-cell"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#647087] mb-1">Business tel</label>
          <PhoneInput
            value={form.bus_tel_no || ""}
            onChange={(v) => setForm({ ...form, bus_tel_no: v })}
            disabled={fieldsLocked}
            testId="emp-tel"
            placeholder="011 234 5678 or +27 11 234 5678"
          />
        </div>
        <Field label="SA ID number" value={form.id_number} disabled={fieldsLocked} onChange={(v) => setForm({ ...form, id_number: v })} testId="emp-id" />
        <Field label="Tax number" value={form.income_tax_number} disabled={fieldsLocked} onChange={(v) => setForm({ ...form, income_tax_number: v })} testId="emp-tax" />
        <Field label="Pay rate (ZAR)" value={form.pay_rate} type="number" disabled={fieldsLocked} onChange={(v) => setForm({ ...form, pay_rate: v })} testId="emp-rate" />
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#647087] mb-1">Pay frequency</label>
          <select
            value={form.pay_frequency}
            onChange={(e) => setForm({ ...form, pay_frequency: e.target.value })}
            disabled={fieldsLocked}
            className="hv-input text-sm w-full disabled:opacity-70"
            data-testid="emp-freq"
          >
            <option value="">—</option>
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="hourly">Hourly</option>
          </select>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <label className="inline-flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={form.is_active} disabled={fieldsLocked} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="emp-active" />
            Active
          </label>
          <label className="inline-flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={form.overtime_exempt} disabled={fieldsLocked} onChange={(e) => setForm({ ...form, overtime_exempt: e.target.checked })} data-testid="emp-ot-exempt" />
            Overtime exempt
          </label>
        </div>
      </div>
      {canEdit && editing && (
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#647087] mb-1">HR notes (never visible to employee)</label>
          <VoiceTextarea
            value={form.notes}
            onChange={(v) => setForm({...form, notes: v})}
            rows={3}
            data-testid="emp-notes"
           testIdPrefix="emp-notes-voice" context="note" />
        </div>
      )}
      {!canEdit && (
        <div className="text-[11px] text-[#647087] inline-flex items-center gap-1">
          <Lock className="w-3 h-3" /> Only owners and admins can edit HR fields.
        </div>
      )}
      {canEdit && editing && (
        <div className="flex justify-end gap-2">
          <button onClick={cancelEdit} disabled={busy} className="hv-btn hv-btn-ghost text-xs" data-testid="emp-cancel">
            Cancel
          </button>
          <button onClick={save} disabled={busy} className="hv-btn hv-btn-primary text-xs disabled:opacity-50" data-testid="emp-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save changes
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, type = "text", disabled, onChange, testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-[#647087] mb-1">{label}</label>
      {type === "date" ? (
        <DatePickerInput
          value={value ?? ""}
          onChange={onChange}
          disabled={disabled}
          testId={testId}
        />
      ) : (
        <input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="hv-input text-sm w-full disabled:opacity-70"
          data-testid={testId}
        />
      )}
    </div>
  );
}

const DOC_KINDS = [
  { value: "id_book", label: "ID / Passport" },
  { value: "drivers", label: "Driver's licence" },
  { value: "medical", label: "Medical cert" },
  { value: "qualification", label: "Qualification" },
  { value: "contract", label: "Contract / Annexure" },
  { value: "training", label: "Training cert" },
  { value: "nda", label: "NDA" },
  { value: "other", label: "Other" },
];

function DocumentsTab({ uid, isSelf, isHr }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const [items, setItems] = useState([]);
  const [ocrDocs, setOcrDocs] = useState([]);
  const [personId, setPersonId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("id_book");
  const [label, setLabel] = useState("");
  const [expiry, setExpiry] = useState("");
  const [uploading, setUploading] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [autoOcr, setAutoOcr] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/team/employees/${uid}/documents`);
      setItems(data.items || []);
    } catch (_e) { /* noop */ }
    finally { setLoading(false); }
  };
  const loadOcrDocs = async () => {
    if (!isHr) return;
    try {
      // Resolve person_id once.
      let pid = personId;
      if (!pid) {
        const r = await api.get(`/team/persons/by-user/${uid}`).catch(() => null);
        pid = r?.data?.person_id || null;
        if (pid) setPersonId(pid);
      }
      if (!pid) { setOcrDocs([]); return; }
      const { data } = await api.get(`/team/hr-intel/person/${pid}/docs`);
      setOcrDocs(data.items || []);
    } catch (_e) { /* noop */ }
  };
  useEffect(() => { load(); loadOcrDocs(); /* eslint-disable-next-line */ }, [uid]);

  const onPick = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const up = await uploadFile(file, { source: "employee_document" });
      await api.post(`/team/employees/${uid}/documents`, {
        kind, label: label || undefined, upload_id: up.id,
        expiry_date: expiry || undefined,
      });
      setLabel(""); setExpiry("");
      toast.success(isHr ? "Document added." : "Uploaded — pending HR review.");
      load();
      // Auto-trigger Hidro OCR ingestion (HR only — backend gates it).
      if (isHr && autoOcr) {
        setOcrBusy(true);
        try {
          await api.post(`/team/hr-intel/ingest/${up.id}`, {
            kind,
            employee_user_id: uid,
          });
          toast.success(`${aiName} OCR done — searchable via the global ${aiName} AI.`);
          loadOcrDocs();
        } catch (e) {
          // Non-blocking — the doc upload itself succeeded.
          toast.error(e?.response?.data?.detail || "OCR failed (doc still saved).");
        } finally { setOcrBusy(false); }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  const reOcr = async (uploadId) => {
    if (!isHr) return;
    setOcrBusy(true);
    try {
      await api.post(`/team/hr-intel/ingest/${uploadId}`, { employee_user_id: uid });
      toast.success("Re-OCR'd.");
      loadOcrDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OCR failed.");
    } finally { setOcrBusy(false); }
  };

  const removeDoc = async (doc) => {
    if (!confirm(`Delete document "${doc.label || doc.kind}"?`)) return;
    try {
      await api.delete(`/team/employees/${uid}/documents/${doc.id}`);
      toast.success("Removed.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  // Build a lookup of OCR'd records keyed by upload_id so we can mark rows.
  const ocrByUpload = ocrDocs.reduce((m, d) => { m[d.upload_id] = d; return m; }, {});

  return (
    <div className="space-y-3" data-testid="documents-tab">
      <div className="hv-card p-4">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <div className="text-xs font-medium text-white inline-flex items-center gap-1"><Upload className="w-3.5 h-3.5" /> Add document</div>
          <div className="flex items-center gap-2">
            {isSelf && !isHr && (
              <span className="text-[10px] text-amber-200">Self-uploads are pending HR review.</span>
            )}
            {isHr && (
              <label className="text-[10px] text-[#a0aabf] inline-flex items-center gap-1 cursor-pointer" title={`When ON, each upload is also OCR'd by ${aiName} and added to the global AI's HR knowledge.`}>
                <input
                  type="checkbox"
                  checked={autoOcr}
                  onChange={(e) => setAutoOcr(e.target.checked)}
                  data-testid="doc-auto-ocr"
                />
                Auto-OCR with {aiName}
              </label>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#647087] mb-1">Type</label>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="hv-input text-sm w-full" data-testid="doc-kind">
              {DOC_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </div>
          <Field label="Label (optional)" value={label} onChange={setLabel} testId="doc-label" />
          <Field label="Expiry date (optional)" value={expiry} type="date" onChange={setExpiry} testId="doc-expiry" />
          <label className="hv-btn hv-btn-primary text-xs justify-center cursor-pointer disabled:opacity-50" data-testid="doc-upload">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? "Uploading…" : "Choose file"}
            <input type="file" className="hidden" disabled={uploading} onChange={(e) => onPick(e.target.files?.[0])} />
          </label>
        </div>
      </div>

      {loading ? (
        <div className="h-16 bg-white/[0.04] rounded animate-pulse" />
      ) : items.length === 0 ? (
        <div className="hv-card p-6 text-center text-xs text-[#647087]" data-testid="docs-empty">
          No documents on file yet.
        </div>
      ) : (
        <div className="space-y-1" data-testid="docs-list">
          {items.map((d) => {
            const expired = d.expiry_date && new Date(d.expiry_date) < new Date();
            const soonExpire = d.expiry_date && !expired && (new Date(d.expiry_date) - new Date()) < 30 * 86400000;
            const downloadUrl = uploadUrl ? uploadUrl(d.upload_id) : `/api/uploads/${d.upload_id}`;
            const ocrRow = ocrByUpload[d.upload_id];
            return (
              <div key={d.id} className="hv-card p-3 flex items-center gap-3 flex-wrap" data-testid={`doc-${d.id}`}>
                <FileText className="w-4 h-4 text-[#1FB6C1] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{d.label || d.kind}</div>
                  <div className="text-[10px] text-[#647087]">
                    {DOC_KINDS.find((x) => x.value === d.kind)?.label || d.kind} ·
                    {" "}{d.status === "pending" ? "Pending HR review" : "Approved"}
                    {d.uploaded_by_name ? ` · uploaded by ${d.uploaded_by_name}` : ""}
                  </div>
                  {d.expiry_date && (
                    <div className={`text-[10px] mt-0.5 inline-flex items-center gap-1 ${expired ? "text-red-300" : soonExpire ? "text-amber-300" : "text-[#647087]"}`}>
                      <Calendar className="w-2.5 h-2.5" /> Expires {new Date(d.expiry_date).toLocaleDateString("en-ZA")}
                      {expired ? " (expired)" : soonExpire ? " (renew soon)" : ""}
                    </div>
                  )}
                  {isHr && ocrRow && (
                    <div
                      className="text-[10px] mt-0.5 text-emerald-300 inline-flex items-center gap-1"
                      title={ocrRow.summary || ""}
                      data-testid={`doc-ocr-badge-${d.id}`}
                    >
                      <CheckCircle2 className="w-2.5 h-2.5" /> {aiName}-OCR'd
                      {ocrRow.doc_type ? ` · ${ocrRow.doc_type}` : ""}
                    </div>
                  )}
                </div>
                {isHr && !ocrRow && (
                  <button
                    onClick={() => reOcr(d.upload_id)}
                    disabled={ocrBusy}
                    className="hv-btn hv-btn-ghost text-xs text-[#1FB6C1] disabled:opacity-50"
                    title={`Run ${aiName} OCR on this document`}
                    data-testid={`doc-ocr-${d.id}`}
                  >
                    {ocrBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : "OCR"}
                  </button>
                )}
                <a href={downloadUrl} target="_blank" rel="noreferrer" className="hv-btn hv-btn-ghost text-xs" data-testid={`doc-download-${d.id}`}>
                  <Download className="w-3 h-3" />
                </a>
                <button onClick={() => removeDoc(d)} className="hv-btn hv-btn-ghost text-xs text-red-300 hover:text-red-200" data-testid={`doc-delete-${d.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function JobCardsTab({ uid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/team/employees/${uid}/jobcards`);
        if (alive) setItems(data.items || []);
      } catch (_e) { /* noop */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [uid]);
  if (loading) return <div className="h-20 bg-white/[0.04] rounded animate-pulse" />;
  if (items.length === 0) return <div className="hv-card p-6 text-center text-xs text-[#647087]" data-testid="jc-empty">No job cards on record.</div>;
  return (
    <div className="space-y-1" data-testid="employee-jc-list">
      {items.map((j) => (
        <Link key={j.id} to={`/jobcards/${j.id}`} className="hv-card p-3 block hover:border-[#1FB6C1]/40" data-testid={`employee-jc-${j.id}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate">
                <span className="text-[#1FB6C1]">{j.short_id || j.id.slice(0, 8)}</span> · {j.title || "(no title)"}
              </div>
              <div className="text-[10px] text-[#647087]">{j.customer_name || ""} · {j.phase}</div>
            </div>
            {j.scheduled_at && <span className="text-[10px] text-[#647087]">{new Date(j.scheduled_at).toLocaleDateString("en-ZA")}</span>}
          </div>
        </Link>
      ))}
    </div>
  );
}

function PayslipsTab({ uid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/team/employees/${uid}/payslips`);
        if (alive) setItems(data.items || []);
      } catch (_e) { /* noop */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [uid]);
  if (loading) return <div className="h-20 bg-white/[0.04] rounded animate-pulse" />;
  if (items.length === 0) return <div className="hv-card p-6 text-center text-xs text-[#647087]" data-testid="payslips-empty">No payslips synced yet. Owner can pull them from SimplePay.</div>;
  return (
    <div className="space-y-1" data-testid="payslips-list">
      {items.map((p) => (
        <div key={p.id} className="hv-card p-3 flex items-center justify-between gap-2 flex-wrap" data-testid={`payslip-${p.id}`}>
          <div>
            <div className="text-sm text-white">Payslip · {p.date ? new Date(p.date).toLocaleDateString("en-ZA") : "—"}</div>
            <div className="text-[10px] text-[#647087]">
              SimplePay #{p.simplepay_payslip_id}
              {p.finalised ? " · finalised" : " · draft"}
              {p.special ? " · special run" : ""}
            </div>
          </div>
          {p.finalised && (
            <span className="text-[10px] text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> finalised</span>
          )}
        </div>
      ))}
    </div>
  );
}

function GrantPointsButton({ uid, name }) {
  const [busy, setBusy] = useState(false);
  const grant = async () => {
    const raw = window.prompt(`Grant points to ${name}\n\nEnter amount (+ or -) and reason, e.g. "+25 great customer feedback":`);
    if (!raw) return;
    const m = raw.trim().match(/^(-?\d+)\s+(.+)$/);
    if (!m) { toast.error('Format: "+25 reason" or "-10 reason"'); return; }
    const points = parseInt(m[1], 10);
    const reason = m[2].trim();
    if (!points || !reason) { toast.error("Need both a number and a reason."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/team/rewards/grant", { user_id: uid, points, reason });
      toast.success(`Granted ${points >= 0 ? "+" : ""}${points} pts. New balance: ${data.balance}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed.");
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={grant}
      disabled={busy}
      className="hv-btn hv-btn-ghost text-xs disabled:opacity-50"
      data-testid="employee-grant-points"
      title="Award or deduct points"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trophy className="w-3.5 h-3.5" />} Grant points
    </button>
  );
}

